import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/solid';

const RenameAccountModal = ({ isOpen, onClose, account, onSave }) => {
  const [accountName, setAccountName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Sync input when modal opens or account changes
  useEffect(() => {
    if (isOpen && account) {
      setAccountName(account.name || '');
      setError('');
    }
  }, [isOpen, account]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmed = accountName.trim();
    if (!trimmed) {
      setError('Account name is required');
      return;
    }

    if (trimmed === (account?.name || '')) {
      onClose();
      return;
    }

    setSubmitting(true);
    try {
      await onSave(trimmed);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-[#1F2937] rounded-card shadow-elevated max-w-md w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b border-[#E5E7EB] dark:border-[#374151] p-6 flex items-center justify-between">
            <h2 className="text-xl font-bold text-[#111827] dark:text-[#F9FAFB]">Rename account</h2>
            <button
              onClick={handleClose}
              disabled={submitting}
              className="btn-icon text-[#6B7280] dark:text-[#9CA3AF] hover:text-[#111827] dark:hover:text-[#F9FAFB] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#111827] dark:text-[#F9FAFB] mb-2">
                Account name
              </label>
              <input
                type="text"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                className="w-full px-4 py-2 bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-card text-[#111827] dark:text-[#F9FAFB] focus:ring-2 focus:ring-[#3B82F6] dark:focus:ring-[#60A5FA] focus:border-transparent"
                placeholder="e.g., My Trading Account"
                disabled={submitting}
                autoFocus
              />
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-card p-3">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] text-[#111827] dark:text-[#F9FAFB] rounded-card hover:bg-[#F3F4F6] dark:hover:bg-[#374151] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-[#3B82F6] dark:bg-[#60A5FA] text-white rounded-card hover:bg-[#2563EB] dark:hover:bg-[#3B82F6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </span>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default RenameAccountModal;
